import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MasonryGrid, type Photo } from "../components/portfolio/MasonryGrid";
import { Lightbox } from "../components/portfolio/Lightbox";
import { Button } from "../components/common/Button";
import { getTopRankedImages } from "../data/projectLoader";
import { useLanguage } from "../contexts/LanguageContext";
import styles from "./Home.module.css";

export const Home: React.FC = () => {
  const [recentPhotos, setRecentPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { t } = useLanguage();

  useEffect(() => {
    const loadRecentWork = async () => {
      setLoading(true);
      try {
        // getTopRankedImages already filters out share projects
        const rankedPhotos = await getTopRankedImages(12);
        setRecentPhotos(rankedPhotos);
      } catch (error) {
        console.error("Failed to load recent work:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRecentWork();
  }, []);

  const handlePhotoClick = (photo: Photo) => {
    const index = recentPhotos.findIndex((p) => p.id === photo.id);
    setCurrentIndex(index);
    setSelectedPhoto(photo);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handleNext = () => {
    if (recentPhotos.length === 0) return;
    const nextIndex = (currentIndex + 1) % recentPhotos.length;
    setCurrentIndex(nextIndex);
    setSelectedPhoto(recentPhotos[nextIndex]);
  };

  const handlePrev = () => {
    if (recentPhotos.length === 0) return;
    const prevIndex =
      (currentIndex - 1 + recentPhotos.length) % recentPhotos.length;
    setCurrentIndex(prevIndex);
    setSelectedPhoto(recentPhotos[prevIndex]);
  };

  return (
    <div className={styles.home}>
      {/* Recent Work Gallery */}
      <section className={styles.recentWork}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.loadingSpinner} />
            <p>{t("home.loading")}</p>
          </div>
        ) : recentPhotos.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{t("home.noRecentWork")}</p>
          </div>
        ) : (
          <MasonryGrid
            photos={recentPhotos}
            columns={4}
            staggerDelay={60}
            onPhotoClick={handlePhotoClick}
          />
        )}

        <div className={styles.viewAllContainer}>
          <Link to="/portfolio">
            <Button variant="primary" size="large">
              {t("home.viewFullPortfolio")}
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>{t("home.readyToCapture")}</h2>
          <p className={styles.ctaText}>{t("home.captureYourStory")}</p>
          <Link to="/contact">
            <Button variant="primary" size="large">
              {t("home.getInTouch")}
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {selectedPhoto && (
        <Lightbox
          photo={selectedPhoto}
          onClose={handleClose}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      )}
    </div>
  );
};
